import React, { useState } from 'react'
import { Button, Pill, ConfirmDialog } from '../ui'
import { useReflections } from './hooks/useReflections.js'
import { ReflectionForm } from './ReflectionForm.jsx'

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export function ReflectionsPage({ me, ownerUserId = null, onSelectReflection, onNewReflection }) {
  const {
    reflections, loading, error, createReflection, deleteReflection,
  } = useReflections({ ownerUserId, enabled: true })

  const [showForm, setShowForm] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)

  const isManagerView = !!ownerUserId
  // Managers only see reflections that the employee has marked ready (or closed)
  const visibleReflections = isManagerView
    ? reflections.filter((r) => r.readyAt || r.closedAt)
    : reflections
  const openReflections = visibleReflections.filter((r) => r.status === 'open')
  const closedReflections = visibleReflections.filter((r) => r.status === 'closed')
  const [closedExpanded, setClosedExpanded] = useState(false)

  async function handleCreate(body) {
    const reflection = await createReflection(body)
    setShowForm(false)
    onSelectReflection?.(reflection.reflectionId)
  }

  async function handleDelete() {
    if (!deleteTarget) return
    await deleteReflection(deleteTarget)
    setDeleteTarget(null)
  }

  function renderCard(reflection) {
    const isClosed = reflection.status === 'closed'
    const isReady = !!reflection.readyAt
    const isShared = !!reflection.sharedAt

    return (
      <div
        key={reflection.reflectionId}
        style={{ ...styles.card, ...(isClosed ? styles.cardClosed : {}) }}
        onClick={() => onSelectReflection?.(reflection.reflectionId)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onSelectReflection?.(reflection.reflectionId)}
      >
        <div style={styles.cardHeader}>
          <span style={styles.cardTitle}>{reflection.title}</span>
          <div style={styles.cardTrailing}>
            {isClosed && <Pill tone="neutral">Closed</Pill>}
            {!isClosed && !isReady && <Pill tone="neutral">Draft</Pill>}
            {!isClosed && isReady && <Pill tone="info">Ready</Pill>}
            {isShared && <Pill tone="success">Shared</Pill>}
          </div>
        </div>
        {(reflection.periodStart || reflection.periodEnd) && (
          <span style={styles.cardPeriod}>
            {reflection.periodStart && formatDate(reflection.periodStart)}
            {reflection.periodStart && reflection.periodEnd && ' \u2013 '}
            {reflection.periodEnd && formatDate(reflection.periodEnd)}
          </span>
        )}
      </div>
    )
  }

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <div>
          <h2 style={styles.pageTitle}>Reflections</h2>
          <p style={styles.summary}>
            {openReflections.length} open \u00B7 {closedReflections.length} closed
          </p>
        </div>
        {!showForm && (
          <Button onClick={() => onNewReflection ? onNewReflection() : setShowForm(true)}>
            New reflection
          </Button>
        )}
      </div>

      {showForm && (
        <div style={styles.formWrapper}>
          <ReflectionForm
            ownerUserId={ownerUserId}
            existingTitles={openReflections.map((r) => r.title)}
            onSave={handleCreate}
            onCancel={() => setShowForm(false)}
          />
        </div>
      )}

      {error && <p style={styles.error}>Failed to load reflections.</p>}
      {loading && <p style={styles.muted}>Loading...</p>}

      {!loading && (
        <>
          <div style={styles.group}>
            <div style={styles.groupHeader}>
              <span style={styles.groupLabel}>Open</span>
              <span style={styles.groupCount}>{openReflections.length}</span>
            </div>
            <div style={styles.groupItems}>
              {openReflections.map(renderCard)}
              {openReflections.length === 0 && (
                <p style={styles.empty}>No open reflections.</p>
              )}
            </div>
          </div>

          {closedReflections.length > 0 && (
            <div style={styles.group}>
              <button style={styles.groupHeaderBtn} onClick={() => setClosedExpanded((c) => !c)}>
                <span style={styles.groupLabel}>Closed</span>
                <span style={styles.groupCount}>{closedReflections.length}</span>
                <span style={styles.groupToggle}>{closedExpanded ? '\u25B2' : '\u25BC'}</span>
              </button>
              {closedExpanded && (
                <div style={styles.groupItems}>
                  {closedReflections.map(renderCard)}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete reflection"
        message="Delete this reflection? This cannot be undone."
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}

const styles = {
  page: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '32px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  pageTitle: { fontSize: '22px', fontWeight: 700, letterSpacing: '-0.02em', margin: 0 },
  summary: { fontSize: '12px', color: 'var(--text-secondary)', margin: '4px 0 0 0' },
  formWrapper: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '10px',
    padding: '20px',
  },
  group: { display: 'flex', flexDirection: 'column', gap: '10px' },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 0',
  },
  groupHeaderBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'none',
    border: 'none',
    padding: '4px 0',
    cursor: 'pointer',
    textAlign: 'left',
  },
  groupLabel: {
    fontSize: '10px',
    fontWeight: 700,
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  groupCount: { fontSize: '10px', color: 'var(--text-secondary)' },
  groupToggle: { fontSize: '10px', color: 'var(--text-secondary)', marginLeft: 'auto', cursor: 'pointer' },
  groupItems: { display: 'flex', flexDirection: 'column', gap: '10px' },
  card: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '10px',
    padding: '14px 16px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  cardClosed: { opacity: 0.6 },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
  },
  cardTitle: { fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' },
  cardTrailing: { display: 'flex', gap: '4px', flexShrink: 0 },
  cardPeriod: { fontSize: '12px', color: 'var(--text-secondary)' },
  empty: { fontSize: '12px', color: 'var(--text-secondary)', margin: 0 },
  muted: { fontSize: '13px', color: 'var(--text-secondary)' },
  error: { fontSize: '13px', color: '#c45a5a' },
}
